/**
 * oathera-passport-governance rollout task 21.2: the canonical, deterministic derivation of the
 * activation `preflightDigest` (design §6.5 step 6 and §8 HTTP contracts). The digest is a stable
 * 64-lowercase-hex SHA-256 fingerprint of the governance-relevant evidence in the
 * {@link ActivationPreflight} report that `getActivationPreflight()` already returns. It is computed
 * entirely client-side over that existing response — no server change derives it — and carried
 * preflight -> prepare -> (later) seal so the same preflight state reproducibly yields the same
 * digest.
 *
 * Canonicalization (the design decision recorded in
 * `.agents/tasks/activation-prepare/plan.md`, option A — a pure function of the report):
 *
 *   1. Select only the governance-relevant, server-authored fields of the report:
 *      - scalars: tenantId, ok, riskAffectedCount, ownerAffectedCount,
 *        activationContentAffectedCount, projectionUnaffectedCount, immediateManagerChangeCount,
 *        deferredManagerChangeCount.
 *      - managerSwitches: each element reduced to {agent, from, to}, then the array sorted by
 *        agent, then from, then to (null sorts before a string).
 *      - deferredManagerSwitches: each element reduced to {agent, pair}, sorted by agent then pair
 *        (null before string).
 *      - blockers: each element reduced to {agent?, reason, pairDigest?} (optional keys emitted
 *        only when present), sorted by reason then agent then pairDigest.
 *      Sorting the arrays makes the digest independent of server row order, which is not
 *      contractually stable, so "same preflight state" is well-defined.
 *   2. Serialize with {@link canonicalJson}: object keys emitted in sorted order, arrays in the
 *      order fixed by step 1, no insignificant whitespace, standard JSON scalar encoding.
 *   3. Domain-separate and hash: SHA-256 over
 *      `"oathera:passport-governance:preflight-digest:v1\u0000" + canonicalJson`, output as
 *      lowercase hex matching /^[0-9a-f]{64}$/. The `v1` tag lets a future canonicalization be
 *      distinguished; the prefix mirrors the `governanceSessionRef` convention in
 *      `apps/ui/src/worker/governance-step-up.ts` so this digest can never collide with another
 *      domain's hash of the same bytes.
 */
import type { ActivationPreflight } from "./passport-governance-client.ts";

const DOMAIN_PREFIX = "oathera:passport-governance:preflight-digest:v1\u0000";
const encoder = new TextEncoder();

/** Order two values where `null` sorts before any string, and strings compare lexicographically. */
function compareNullableString(a: string | null, b: string | null): number {
  if (a === b) return 0;
  if (a === null) return -1;
  if (b === null) return 1;
  return a < b ? -1 : 1;
}

/**
 * Reduce an {@link ActivationPreflight} report to the governance-relevant shape that is hashed. The
 * scalars are copied verbatim; the three arrays are reduced to their governance-relevant keys and
 * sorted into a deterministic order (the server does not guarantee row order).
 */
export function canonicalizePreflight(report: ActivationPreflight): unknown {
  const managerSwitches = report.managerSwitches
    .map((s) => ({ agent: s.agent, from: s.from, to: s.to }))
    .sort(
      (a, b) =>
        compareNullableString(a.agent, b.agent) ||
        compareNullableString(a.from, b.from) ||
        compareNullableString(a.to, b.to),
    );
  const deferredManagerSwitches = report.deferredManagerSwitches
    .map((s) => ({ agent: s.agent, pair: s.pair }))
    .sort(
      (a, b) => compareNullableString(a.agent, b.agent) || compareNullableString(a.pair, b.pair),
    );
  const blockers = report.blockers
    .map((b) => ({
      ...(b.agent === undefined ? {} : { agent: b.agent }),
      reason: b.reason,
      ...(b.pairDigest === undefined ? {} : { pairDigest: b.pairDigest }),
    }))
    .sort(
      (a, b) =>
        compareNullableString(a.reason, b.reason) ||
        compareNullableString(a.agent ?? null, b.agent ?? null) ||
        compareNullableString(a.pairDigest ?? null, b.pairDigest ?? null),
    );
  return {
    tenantId: report.tenantId,
    ok: report.ok,
    riskAffectedCount: report.riskAffectedCount,
    ownerAffectedCount: report.ownerAffectedCount,
    activationContentAffectedCount: report.activationContentAffectedCount,
    projectionUnaffectedCount: report.projectionUnaffectedCount,
    immediateManagerChangeCount: report.immediateManagerChangeCount,
    deferredManagerChangeCount: report.deferredManagerChangeCount,
    managerSwitches,
    deferredManagerSwitches,
    blockers,
  };
}

/**
 * A stable canonical JSON serialization: object keys are emitted in sorted order, arrays keep their
 * (already deterministic) order, and there is no insignificant whitespace. Scalars use the standard
 * `JSON.stringify` encoding. `undefined` values and function/symbol values are dropped, matching
 * `JSON.stringify` for objects.
 */
export function canonicalJson(value: unknown): string {
  if (value === null || typeof value !== "object") {
    return JSON.stringify(value) ?? "null";
  }
  if (Array.isArray(value)) {
    return `[${value.map((v) => canonicalJson(v === undefined ? null : v)).join(",")}]`;
  }
  const record = value as Record<string, unknown>;
  const keys = Object.keys(record)
    .filter((k) => record[k] !== undefined)
    .sort();
  const entries = keys.map((k) => `${JSON.stringify(k)}:${canonicalJson(record[k])}`);
  return `{${entries.join(",")}}`;
}

/** Lowercase-hex encode bytes. */
function toHex(bytes: Uint8Array): string {
  return [...bytes].map((b) => b.toString(16).padStart(2, "0")).join("");
}

/**
 * Compute the deterministic 64-lowercase-hex `preflightDigest` for a report. Same preflight state
 * yields the same digest; a changed governance-relevant field yields a different one. The operator
 * never hand-types this — it is computed from the preflight response and fed into prepare.
 */
export async function computePreflightDigest(report: ActivationPreflight): Promise<string> {
  const json = canonicalJson(canonicalizePreflight(report));
  const input = encoder.encode(`${DOMAIN_PREFIX}${json}`);
  const digest = new Uint8Array(await crypto.subtle.digest("SHA-256", input));
  return toHex(digest);
}
